import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { SessionProvider, useSession } from "./lib/session.tsx";
import { Loading } from "./lib/loading.tsx";
import { ThemeProvider } from "./lib/use-theme.tsx";
import { SignIn } from "./features/auth/SignIn.tsx";
import { AuthErrorBanner } from "./features/auth/AuthErrorBanner.tsx";
import { LessonsPage } from "./features/lessons/LessonsPage.tsx";
import { StudentsPage } from "./features/lessons/StudentsPage.tsx";
import { StudentPage } from "./features/students/StudentPage.tsx";
import { JoinPage } from "./features/join/JoinPage.tsx";
import { LessonPage } from "./features/room/LessonPage.tsx";
import { ArchivePage } from "./features/archive/ArchivePage.tsx";
import { ArchiveListPage } from "./features/archive/ArchiveListPage.tsx";
import { HomeworkListPage } from "./features/homework/HomeworkListPage.tsx";
import { HomeworkPage } from "./features/homework/HomeworkPage.tsx";
import { AssignHomeworkPage } from "./features/homework/AssignHomeworkPage.tsx";
import { ConversationsPage } from "./features/announcements/ConversationsPage.tsx";
import { ConversationPage } from "./features/announcements/ConversationPage.tsx";
import { MyMaterialsPage } from "./features/materials/MyMaterialsPage.tsx";
import { PlansPage } from "./features/plans/PlansPage.tsx";
import { PlanEditorPage } from "./features/plans/PlanEditorPage.tsx";
import { PlanLessonsPage } from "./features/plans/PlanLessonsPage.tsx";
import { SchedulePage } from "./features/schedule/SchedulePage.tsx";
import { SeriesListPage } from "./features/schedule/SeriesListPage.tsx";
import { SeriesEditorPage } from "./features/schedule/SeriesEditorPage.tsx";
import { Shell } from "./features/shell/Shell.tsx";
import { FeedbackProvider } from "./features/feedback/feedback-context.tsx";
import { ViewBar } from "./features/view/ViewBar.tsx";
import { ViewPage } from "./features/view/ViewPage.tsx";
import { TeacherInvitePage } from "./features/auth/TeacherInvitePage.tsx";

/**
 * Материалы ленивым чанком: экран тянет загрузчик, проверку файлов и плеер, а
 * стартовый экран при 128 кбит/с обязан открыться раньше всего этого.
 */
const MediaPage = lazy(() =>
  import("./features/media/MediaPage.tsx").then((m) => ({ default: m.MediaPage })),
);

/**
 * Экран нарезки документа отдельным ленивым чанком: он нужен ровно тем, кто
 * пришёл собирать заготовку из PDF, и тянуть его в стартовый чанк ради этого
 * незачем.
 */
const ImportPage = lazy(() =>
  import("./features/import/ImportPage.tsx").then((m) => ({ default: m.ImportPage })),
);

/**
 * Озвучка текста ленивым чанком, и это требование, а не экономия. Экран целиком
 * преподавательский (платит наш ключ, раздел 11.7 docs/tts-design.md), а
 * статический импорт увёз бы его в стартовый чанк и повёз бы каждому ученику
 * (раздел 5.1 CLAUDE.md: чанкование держится исключительно на `import()`).
 */
const TtsPage = lazy(() =>
  import("./features/tts/TtsPage.tsx").then((m) => ({ default: m.TtsPage })),
);

/**
 * Карта покрытия ОГЭ ленивым чанком: вместе с экраном едет весь каркас тем
 * (словарь навыков, граф, раскладка), а нужен он только тому, кто открыл
 * карту. Статический импорт вытащил бы каркас в общий бандл (раздел 5.1
 * CLAUDE.md: чанкование держится исключительно на `import()`).
 */
const CoverageMapPage = lazy(() =>
  import("./features/coverage/CoverageMapPage.tsx").then((m) => ({
    default: m.CoverageMapPage,
  })),
);

/**
 * «Что нового» ленивым чанком, и это требование, а не экономия. Вместе с
 * экраном едет сам список записей, а он не показывается ученику вовсе:
 * статический импорт вытащил бы его в стартовый чанк и повёз бы каждому
 * (раздел 5.1 CLAUDE.md: чанкование держится исключительно на `import()`).
 */
const WhatsNewPage = lazy(() =>
  import("./features/changelog/WhatsNewPage.tsx").then((m) => ({ default: m.WhatsNewPage })),
);

/**
 * Предполётная проверка ленивым чанком, и это требование, а не экономия. Экран
 * нужен меньшинству и не на каждом входе, а стартовый чанк обязан открыться на
 * канале 128 кбит/с раньше всего остального (раздел 5.1 CLAUDE.md: чанкование
 * держится исключительно на `import()`).
 *
 * ⚠️ Чанк обязан оставаться маленьким, и это не общее пожелание. Проверка
 * диагностирует состояние сети, в котором может не доехать сама (пункт 10
 * раздела 10 `docs/room-preflight-design.md`): тяжёлая зависимость здесь
 * означает экран, который не открывается ровно тогда, когда он нужен.
 */
/**
 * Группы ленивым чанком, и это требование, а не экономия. Оба экрана целиком
 * преподавательские: статический импорт увёз бы их в стартовый чанк и повёз бы
 * каждому ученику (раздел 5.1 CLAUDE.md, чанкование держится исключительно на
 * `import()`).
 */
const GroupsPage = lazy(() =>
  import("./features/groups/GroupsPage.tsx").then((m) => ({ default: m.GroupsPage })),
);
const GroupPage = lazy(() =>
  import("./features/groups/GroupPage.tsx").then((m) => ({ default: m.GroupPage })),
);

/**
 * Расписание группы тем же ленивым чанком и по той же причине: экран целиком
 * преподавательский, ученику все ручки пучка отвечают 404 как постороннему.
 * Статический импорт увёз бы его в стартовый чанк и повёз бы каждому ученику
 * (раздел 5.1 CLAUDE.md, чанкование держится исключительно на `import()`).
 */
const GroupSchedulePage = lazy(() =>
  import("./features/schedule/group/GroupSchedulePage.tsx").then((m) => ({
    default: m.GroupSchedulePage,
  })),
);

/**
 * Форма отправки сообщения ленивым чанком по той же причине: её видит только
 * преподаватель, а вместе с ней едут список групп, список учеников и выбор
 * адресата.
 */
const ComposeAnnouncementPage = lazy(() =>
  import("./features/announcements/teacher/index.ts").then((m) => ({
    default: m.ComposeAnnouncementPage,
  })),
);

/**
 * Старые адреса проверки (перевод и ответ текстом) перенаправляют на экран
 * результатов домашки с якорем на часть: с 26.09.2026 проверка идёт там, на
 * одном экране с одной кнопкой возврата (1.10 docs/homework-scratch-design.md).
 * Ссылки из уведомлений, закладок и истории живут дальше.
 */
function ReviewRedirect(props: { part: "exercise" | "answer" }) {
  const { homeworkId = "", elementId = "" } = useParams();
  const location = useLocation();
  const hash = props.part === "answer" ? "#answer" : `#ex-${encodeURIComponent(elementId)}`;
  // Состояние и строка запроса едут дальше: по ним «Закрыть» ведёт туда же,
  // откуда пришли (разрез списка по ученику, `backToOf` в HomeworkPage)
  return (
    <Navigate
      replace
      to={{ pathname: `/homework/${homeworkId}`, search: location.search, hash }}
      state={location.state}
    />
  );
}

/**
 * Карточки ленивыми чанками, двумя: экраны ученика (список, тренировка,
 * пролистывание) и колоды преподавателя (10 `docs/flashcards-design.md`). Вместе
 * с экранами ученика едут `@edu/shared/flashcards` и `ts-fsrs`, и в стартовый
 * чанк им нельзя (5.1 и 5.3 CLAUDE.md). Каркас кабинета предзагружает чанк
 * ученика, только когда показать сегодня есть что.
 */
const FlashcardsPage = lazy(() =>
  import("./features/flashcards/student-screens.ts").then((m) => ({ default: m.FlashcardsPage })),
);
const TrainerPage = lazy(() =>
  import("./features/flashcards/student-screens.ts").then((m) => ({ default: m.TrainerPage })),
);
const BrowsePage = lazy(() =>
  import("./features/flashcards/student-screens.ts").then((m) => ({ default: m.BrowsePage })),
);
const DecksPage = lazy(() =>
  import("./features/flashcards/teacher-screens.ts").then((m) => ({ default: m.DecksPage })),
);
const DeckPage = lazy(() =>
  import("./features/flashcards/teacher-screens.ts").then((m) => ({ default: m.DeckPage })),
);
const DeckPreviewPage = lazy(() =>
  import("./features/flashcards/teacher-screens.ts").then((m) => ({ default: m.DeckPreviewPage })),
);

/**
 * Ссылка для преподавателей на звонок ленивым чанком (6.4
 * `docs/teacher-calls-design.md`): её открывают единицы, и стартовому чанку
 * экран ни к чему. Комнату он не тянет: `features/calls` из `room` и `board`
 * не импортирует ничего.
 */
const CallLinkPage = lazy(() =>
  import("./features/calls/CallLinkPage.tsx").then((m) => ({ default: m.CallLinkPage })),
);

const PreflightPage = lazy(() =>
  import("./features/preflight/PreflightPage.tsx").then((m) => ({ default: m.PreflightPage })),
);

/**
 * Маршруты.
 *
 * Настоящие адреса, а не состояние в useState, и это обязательное следствие
 * модели приглашений: постоянная ссылка ученика ведёт на `/join/<код>`, и по
 * ней он приходит из мессенджера, а не из нашего интерфейса. Раз есть один
 * входной адрес, остальные экраны тоже обязаны быть адресуемыми: иначе кнопка
 * «назад» в браузере выкидывает с урока, а ссылку на архив нельзя переслать.
 *
 * Комната и архив ленивыми чанками (внутри своих страниц): Excalidraw плюс
 * LiveKit это порядка мегабайта после gzip, и при 128 кбит/с в режиме белого
 * списка (раздел 3.1 CLAUDE.md) стартовый экран обязан открыться раньше них.
 */
export function App() {
  return (
    /*
      Светлая или тёмная. Провайдер стоит ВЫШЕ всего остального, и оба
      ограничения на его место жёсткие.

      Снизу: комната урока, архив и погашение ссылки живут вне каркаса кабинета
      намеренно (см. набор маршрутов ниже), поэтому провайдер, повешенный
      внутри `Shell`, отсутствовал бы ровно на доске, ради которой тему и
      переключают.

      Сверху: ни сессии, ни адреса он не читает. Тема это настройка браузера, а
      не человека (решение владельца 02.09.2026), и работать она обязана до
      входа, на форме входа и у гостя по ссылке.
    */
    <ThemeProvider>
      <BrowserRouter>
        <SessionProvider>
          {/*
            Заметки о неточностях. Место провайдера выбрано двумя ограничениями
            сразу, и ни одно из них не оставляет свободы.

            Снизу: он обязан стоять ВЫШЕ и каркаса кабинета, и комнаты. Комната,
            архив и погашение ссылки живут вне `Shell` намеренно (см. набор
            маршрутов ниже), поэтому провайдер, повешенный внутри `Shell`,
            отсутствовал бы ровно на том экране, ради которого фича и затевается:
            заметку пишут про то, что сломалось на уроке, а не про список
            учеников.

            Сверху: он обязан стоять внутри `SessionProvider`, потому что читает
            сессию (кнопка есть только у преподавателя), и внутри `BrowserRouter`,
            потому что адрес экрана уезжает в заметку через `useLocation`.

            ⚠️ Направление зависимости именно такое, и на нём держится решение из
            `lib/session.tsx`: провайдер стоит НИЖЕ сессии, то есть про выход он
            знать не может, и досылку очереди перед выходом делает `signOut`,
            дёргая модульный слой очереди напрямую.
          */}
          <FeedbackProvider>
            <AppRoutes />
          </FeedbackProvider>
        </SessionProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

/**
 * Маршруты приложения.
 *
 * Отдельным компонентом, потому что полосу про неуехавшие заметки надо рисовать
 * над всеми маршрутами сразу, а сессию она читает хуком.
 *
 * ⚠️ **Окон поверх маршрутов здесь больше нет** (решение владельца 13.09.2026).
 * До 14.09.2026 `/students/:id` встречался в этом файле ДВАЖДЫ: страницей и
 * окном поверх фона, и различало их состояние перехода. Задумано это было как
 * два вида одного содержимого, а вышли два разных экрана: у окна не было ни
 * крошек, ни раздела «Где дыры», ни списка занятий, зато был свой подвал со
 * статусом ссылки. Остался один экран, страница. Вместе с окном ушла и вся
 * машинерия фона (`lib/overlay.ts`): других потребителей у неё не было.
 */
function AppRoutes() {
  const { unsentNotes } = useSession();

  return (
    <>
      {/*
        Просмотр кабинета админом: полоса над всеми маршрутами, и в кабинете, и
        в комнате (раздел 12.6 docs/admin-panel-design.md). Без просмотра не
        рисуется вовсе.
      */}
      <ViewBar />
      {/*
        Заметки, не уехавшие до выхода.

        ⚠️ Полоса живёт ЗДЕСЬ, над всеми маршрутами, а не на форме входа, и это
        не запас прочности. Выходов из приложения два, и приводят они на разные
        экраны: из кабинета `Shell` уводит на корень, то есть на форму входа, а
        на `/join/:code` человек остаётся там же и тут же входит учеником.
        Полоса, живущая только на форме входа, вторую дверь пропустила бы молча,
        а молчать здесь нельзя: окно заметки закрывается словами «Записал».

        Своей полосой, а не `alert()`: нативный диалог блокирует поток, и по
        этой причине его нет ни в одном подтверждении проекта. Класс общий с
        полосой отказа входа, отдельного вида этому сообщению не нужно.
      */}
      {unsentNotes && (
        <p className="auth-banner" role="alert">
          Часть заметок о неточностях не успела уйти и осталась в этом браузере.
          Они отправятся сами, когда вы войдёте снова.
        </p>
      )}
      <Routes>
          {/*
            Комната, архив и погашение ссылки живут ВНЕ каркаса кабинета, и это
            требование, а не экономия. В комнате доска занимает весь экран, а
            острова Excalidraw стоят по всем четырём краям: сайдбару там нет
            места ни при какой раскладке. Погашение ссылки это вообще первый
            экран ученика, у которого кабинета ещё нет.
          */}
          <Route path="/join/:code" element={<JoinPage />} />
          {/*
            Вход в просмотр кабинета по ссылке из панели, `/view#<код>`. Вне
            каркаса: сессии до обмена кода здесь ещё нет, и провайдер сессии на
            этом адресе её намеренно не спрашивает (`lib/session.tsx`).
          */}
          <Route path="/view" element={<ViewPage />} />
          {/*
            Приглашение преподавателя по ссылке из панели, `/teacher-invite#<код>`
            (раздел 9 `docs/admin-panel-design.md`). Вне каркаса: у приглашённого
            кабинета ещё нет. Код во фрагменте, в адрес заметки он не попадает.
          */}
          <Route path="/teacher-invite" element={<TeacherInvitePage />} />
          {/*
            Проверка звука. Вне каркаса кабинета и БЕЗ сессии: ученик приходит
            сюда до того, как погасил ссылку, а часто и до того, как у него
            вообще появился аккаунт (раздел 4.1
            `docs/room-preflight-design.md`).
          */}
          <Route
            path="/check"
            element={
              <Suspense
                fallback={
                  <main className="lobby">
                    <Loading />
                  </main>
                }
              >
                <PreflightPage />
              </Suspense>
            }
          />
          {/*
            Ссылка для преподавателей на звонок, `/call/<id>#<ключ>` (9.3
            `docs/teacher-calls-design.md`). Вне каркаса: экран про вход в
            комнату, а не раздел кабинета. Ключ во фрагменте, в адрес заметки
            он не попадает
          */}
          <Route
            path="/call/:lessonId"
            element={
              <Suspense
                fallback={
                  <main className="lobby">
                    <Loading />
                  </main>
                }
              >
                <CallLinkPage />
              </Suspense>
            }
          />
          <Route path="/lesson/:lessonId" element={<LessonPage />} />
          <Route path="/archive/:lessonId" element={<ArchivePage />} />

          {/*
            Корень это не экран, а развилка: без сессии форма входа, с сессией
            переадресация на уроки.

            Экраном он быть перестал, когда кабинет разошёлся на «Уроки» и
            «Ученики»: у раздела обязан быть свой адрес, а третий адрес,
            показывающий то же, что первый, означал бы, что подсветка раздела
            в сайдбаре иногда не горит ни на чём.

            Расписание, а не уроки: домашней страницей кабинета его назначил
            макет, и это же первый пункт сайдбара. Раздел «Уроки» никуда не
            делся, он второй, и в нём по-прежнему живут уроки без времени.

            ⚠️ Стоит ВНЕ каркаса: без сессии сайдбар не нужен, а с сессией
            здесь ничего не рисуется вовсе. Иначе на корне мелькал бы пустой
            сайдбар, в котором не подсвечен ни один раздел.
          */}
          <Route path="/" element={<Root />} />

          {/*
            Кабинет: общий сайдбар вокруг всех своих экранов. Макетным
            маршрутом, а не обёрткой внутри каждой страницы: так сайдбар не
            перемонтируется при переходе между разделами, то есть не мигает и
            не перезапрашивает квоту на каждый переход.

            Экран входа стоит только на корне. Остальные адреса решают сами,
            что делать без сессии: урок и архив показывают понятный отказ
            вместо подмены экрана.
          */}
          <Route element={<Shell />}>
            {/*
              Расписание домашней страницей кабинета, как в макете. В стартовом
              чанке: обычный экран без доски и без загрузчика, а лишний ленивый
              чанк на канале 128 кбит/с это лишний круг по сети
            */}
            <Route path="/schedule" element={<SchedulePage />} />
            {/*
              Серии отдельными адресами, а не состоянием недели. Неделя отвечает
              на вопрос «что у меня завтра», серия на вопрос «как мы
              договорились», и это разные частоты: в первую заходят каждый день,
              во вторую несколько раз в год. Свой адрес нужен ещё и затем, чтобы
              «назад» из редактора возвращало в список, а не выкидывало из
              кабинета
            */}
            <Route path="/schedule/series" element={<SeriesListPage />} />
            <Route path="/schedule/series/new" element={<SeriesEditorPage />} />
            <Route path="/schedule/series/:seriesId" element={<SeriesEditorPage />} />
            {/*
              Расписание группы своим адресом, а не режимом редактора серии: у
              пучка другой набор полей (нет заметки и нет правки начала), и
              условный редактор на два режима вышел бы длиннее и опаснее двух
              прямых. Сюда же ведёт диалог создания занятия, когда у выбранной
              группы расписание уже заведено: новый день добавляется здесь, где
              написана зона расписания
            */}
            <Route
              path="/schedule/groups/:groupId"
              element={
                <Suspense fallback={<main className="home home--list"><Loading /></main>}>
                  <GroupSchedulePage />
                </Suspense>
              }
            />
            {/*
              Кабинет разошёлся на два раздела. Панели остались теми же, у
              каждой теперь свой адрес и своя шапка
            */}
            <Route path="/lessons" element={<LessonsPage />} />
            <Route path="/students" element={<StudentsPage />} />
            {/*
              Карточка ученика своим адресом, а не состоянием списка: на неё
              ссылаются занятия и архив, её открывают в соседней вкладке, и
              кнопка «назад» обязана возвращать в список, а не выкидывать из
              кабинета. В стартовом чанке: обычный экран без доски и загрузчика
            */}
            <Route path="/students/:studentId" element={<StudentPage />} />
            {/*
              Домашка. Список и форма в стартовом чанке: обычные экраны без
              доски и проверки. Карточка тоже: тяжёлое (лента с checking и
              KaTeX) она грузит ленивым чанком упражнений сама, через
              features/exercises/lazy.ts
            */}
            {/*
              Сообщения. Список разговоров и само окно в стартовом чанке:
              обычные экраны кабинета без доски и без загрузчика, а лишний
              ленивый чанк на канале 128 кбит/с это лишний круг по сети. Оба
              видны обеим сторонам: одна ручка отдаёт и отправленное, и
              пришедшее, а сторону считает сервер. Форма отправки ленивая по
              другой причине, см. объявление чанка выше.

              ⚠️ Ключ разговора СОСТАВНОЙ (`person:<uuid>` либо `group:<uuid>`),
              и двоеточие внутри сегмента пути законно. С `/announcements/new`
              он не конфликтует: точное совпадение маршрутизатор ставит выше
              подстановки, и это то же правило, по которому уживаются
              `/archive` и `/archive/:lessonId`
            */}
            <Route path="/announcements" element={<ConversationsPage />} />
            <Route
              path="/announcements/new"
              element={
                <Suspense fallback={<main className="home home--form"><Loading /></main>}>
                  <ComposeAnnouncementPage />
                </Suspense>
              }
            />
            <Route path="/announcements/:conversationId" element={<ConversationPage />} />
            {/*
              Ссылки и файлы, присланные ученику (`docs/material-receipt-design.md`,
              шаг 3). В стартовом чанке, как домашка и сообщения: обычный экран
              без доски и загрузчика, и открывает его ученик, для которого
              лишний круг по сети на 128 кбит/с дороже всего. Преподавательских
              модулей экран не тянет (`features/materials/` импортирует только
              общий просмотр медиа и каркас). Маршрут открыт любой сессии, как
              «Что нового»: сервер сам отдаёт ровно присланное самому человеку
            */}
            <Route path="/materials" element={<MyMaterialsPage />} />
            {/* Группы: обе страницы преподавательские, поэтому обе ленивые */}
            <Route
              path="/groups"
              element={
                <Suspense fallback={<main className="home home--list"><Loading /></main>}>
                  <GroupsPage />
                </Suspense>
              }
            />
            <Route
              path="/groups/:groupId"
              element={
                <Suspense fallback={<main className="home home--list"><Loading /></main>}>
                  <GroupPage />
                </Suspense>
              }
            />
            {/*
              Карточки: у ученика список, тренировка и пролистывание, у
              преподавателя колоды. Ленивые, разбор у объявления чанков выше.
              Проверки роли на маршрутах нет: ручки отвечают каждому своё
            */}
            <Route path="/flashcards" element={<Lazy kind="list"><FlashcardsPage /></Lazy>} />
            <Route path="/flashcards/train" element={<Lazy kind="form"><TrainerPage /></Lazy>} />
            <Route path="/flashcards/:deckId/browse" element={<Lazy kind="form"><BrowsePage /></Lazy>} />
            <Route path="/decks" element={<Lazy kind="list"><DecksPage /></Lazy>} />
            <Route path="/decks/:deckId" element={<Lazy kind="list"><DeckPage /></Lazy>} />
            <Route path="/decks/:deckId/preview" element={<Lazy kind="form"><DeckPreviewPage /></Lazy>} />
            <Route path="/homework" element={<HomeworkListPage />} />
            <Route path="/homework/new" element={<AssignHomeworkPage />} />
            <Route path="/homework/:homeworkId" element={<HomeworkPage />} />
            <Route
              path="/homework/:homeworkId/review/:elementId"
              element={<ReviewRedirect part="exercise" />}
            />
            <Route path="/homework/:homeworkId/answer-review" element={<ReviewRedirect part="answer" />} />
            {/*
              Список заготовок в стартовом чанке: на нём нет ни Excalidraw, ни
              загрузчика, а лишний ленивый чанк на канале 128 кбит/с это лишний
              круг по сети. Редактор ленивый, и лениво в нём именно то, что
              тяжело
            */}
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/plans/:planId" element={<PlanEditorPage />} />
            {/*
              Уроки по заготовке тоже в стартовом чанке и по той же причине,
              что список: обычный экран кабинета без доски и без загрузчика.
              Адрес вложен в заготовку намеренно, он и есть хлебная крошка;
              сайдбар при этом остаётся развёрнутым, потому что свёрнутым его
              делает точное совпадение с `/plans/:planId` (см. Shell)
            */}
            <Route path="/plans/:planId/lessons" element={<PlanLessonsPage />} />
            {/*
              Список архива в каркасе кабинета, а сам архив урока (`/archive/:id`
              выше) вне его: там доска во весь экран, и сайдбару места нет.
              Конфликта между двумя адресами не возникает, маршрутизатор ставит
              точное совпадение выше вложенного. Экран в стартовом чанке: на нём
              нет ни Excalidraw, ни загрузчика, а лишний ленивый чанк на канале
              128 кбит/с это лишний круг по сети
            */}
            <Route path="/archive" element={<ArchiveListPage />} />
            {/*
              «Что нового». Ленивым чанком вместе с записями: разбор у объявления
              выше. Маршрут открыт всем, у кого есть сессия, и проверки роли на
              нём нет намеренно: прятать текст про появившиеся возможности от
              того, кто набрал адрес руками, значило бы городить правило ради
              случая, которого не бывает. В сайдбаре пункта у ученика нет.
            */}
            <Route
              path="/whats-new"
              element={
                <Suspense
                  fallback={
                    <main className="home home--form">
                      <Loading />
                    </main>
                  }
                >
                  <WhatsNewPage />
                </Suspense>
              }
            />
            <Route
              path="/media"
              element={
                <Suspense
                  fallback={
                    <main className="home home--list">
                      <Loading />
                    </main>
                  }
                >
                  <MediaPage />
                </Suspense>
              }
            />
            <Route
              path="/coverage"
              element={
                <Suspense
                  fallback={
                    <main className="home home--wide">
                      <Loading />
                    </main>
                  }
                >
                  <CoverageMapPage />
                </Suspense>
              }
            />
            {/* Адрес вложен в материалы намеренно: озвучка это ещё один способ
                завести файл, и приходят сюда оттуда же, откуда загружают */}
            <Route
              path="/media/tts"
              element={
                <Suspense
                  fallback={
                    <main className="home home--list">
                      <Loading />
                    </main>
                  }
                >
                  <TtsPage />
                </Suspense>
              }
            />
            <Route
              path="/media/import/:fileId"
              element={
                <Suspense
                  fallback={
                    <main className="home home--list">
                      <Loading />
                    </main>
                  }
                >
                  <ImportPage />
                </Suspense>
              }
            />
            {/*
              Тот же экран выбора страниц, но с целью: страницы встанут в конец
              этой заготовки (docs/pdf-append-design.md, 3.1). Адрес только
              передаёт цель, режим экрана решает ответ сервера (3.9).
              Сегмент `import`, а не `pdf` из проекта: он уже есть в словаре
              адресов заметок (`FEEDBACK_ROUTE_SEGMENTS`), и шаг остаётся
              чисто веб-правкой без `packages/shared`
            */}
            <Route
              path="/plans/:planId/import/:fileId"
              element={
                <Suspense
                  fallback={
                    <main className="home home--list">
                      <Loading />
                    </main>
                  }
                >
                  <ImportPage />
                </Suspense>
              }
            />
          </Route>

          {/* Неизвестный адрес ведёт себя как корень: тем же выбором между
              формой входа и уроками. Вне каркаса по той же причине */}
          <Route path="*" element={<Root />} />
        </Routes>
    </>
  );
}

/** Ожидание ленивого экрана кабинета в его же ширине колонки. */
function Lazy(props: { kind: "list" | "form"; children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <main className={`home home--${props.kind}`}>
          <Loading />
        </main>
      }
    >
      {props.children}
    </Suspense>
  );
}

/**
 * Развилка корня.
 *
 * ⚠️ Отказ входа (`?auth_error=`) переносится на экран уроков ВМЕСТЕ с
 * адресом, а не теряется при переадресации. При `google_other_user` сессия
 * остаётся живой, показывается кабинет, и сообщение, живущее только на форме
 * входа, никто никогда не увидит. Стирает параметр из адреса тот экран,
 * который его показал (`AuthErrorBanner`), и только он: стереть раньше значит
 * стереть до показа.
 */
function Root() {
  const { state } = useSession();
  const location = useLocation();

  if (state.status === "loading") {
    return (
      <main className="lobby">
        <Loading />
      </main>
    );
  }

  if (state.status === "signed") {
    /**
     * Домашняя это «Расписание», как и назначил макет.
     *
     * До появления экрана серий здесь стояли «Уроки», и причина была честной:
     * завести серию мышью было нечем, значит расписание у всех было пустым, а
     * пустая домашняя без единого доступного действия хуже рабочей. Теперь
     * серия заводится с этого же экрана, и пустое расписание объясняет само
     * себя ссылкой на серии.
     *
     * Раздел «Уроки» никуда не делся, он второй в сайдбаре: в нём живут уроки
     * без времени (ручные, старые, из пачки, осиротевшие), и убрав его, мы
     * спрятали бы их насовсем.
     */
    return <Navigate to={{ pathname: "/schedule", search: location.search }} replace />;
  }

  return (
    <>
      <AuthErrorBanner />
      <SignIn />
    </>
  );
}
